import { Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AlertComponent } from '@components';
import { CriminalRecordCheckDocument } from '@models';
import { Store } from '@ngrx/store';
import { UtilService } from '@services';
import {
  createCriminalRecordCheckDocument,
  createCriminalRecordCheckDocumentFailed,
  createCriminalRecordCheckDocumentSuccess,
  fetchLatestCriminalRecordCheckDocument,
  selectCriminalRecordCheckDocument,
  updateCriminalRecordCheckDocument,
  updateCriminalRecordCheckDocumentFailed,
  updateCriminalRecordCheckDocumentSuccess,
} from '@state';

@Component({
  selector: 'app-criminal-record-check',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatLabel,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    AlertComponent,
  ],
  templateUrl: './criminal-record-check.page.html',
  styleUrl: './criminal-record-check.page.scss',
})
export class CriminalRecordCheckPage {
  public criminalCheckForm: FormGroup = new FormGroup({
    version: new FormControl('', [Validators.required]),
    title: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
  });

  private criminalRecordDocument: CriminalRecordCheckDocument | null = null;
  public isEditMode = false;
  public dragActive = false;
  public selectedFile?: File;
  public uploadPreview: SafeResourceUrl | null = null;
  public loading = false;
  public error: string | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private store: Store,
    private utils: UtilService
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectCriminalRecordCheckDocument)
      .subscribe((document) => {
        this.criminalRecordDocument = document;
        if (this.criminalRecordDocument) {
          this.isEditMode = true;
          this.criminalCheckForm.patchValue({
            version: this.criminalRecordDocument.version,
            title: this.criminalRecordDocument.title,
            description: this.criminalRecordDocument.description,
          });
          if (this.criminalRecordDocument.fileUrl) {
            this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.criminalRecordDocument.fileUrl
            );
          }
        } else {
          this.isEditMode = false;
          this.uploadPreview = null;
        }
      });

    this.store.dispatch(fetchLatestCriminalRecordCheckDocument());
  }

  public async onSubmit(): Promise<void> {
    try {
      if (this.criminalCheckForm.invalid) return;
      this.loading = true;
      const criminalRecordCheckDocData: any = this.criminalCheckForm.value;
      await this.saveCriminalRecordCheckDocument(criminalRecordCheckDocData);
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  private async saveCriminalRecordCheckDocument(
    criminalRecordCheckDoc: CriminalRecordCheckDocument
  ): Promise<void> {
    if (this.isEditMode)
      await this.updateCriminalRecordDocument(criminalRecordCheckDoc);
    else await this.createCriminalRecordDocument(criminalRecordCheckDoc);
  }

  private async createCriminalRecordDocument(
    criminalRecordCheckDocument: CriminalRecordCheckDocument
  ): Promise<void> {
    await this.utils.actionsToPromise(
      createCriminalRecordCheckDocument({
        criminalRecordCheckDocument,
        file: this.selectedFile,
      }),
      createCriminalRecordCheckDocumentSuccess,
      createCriminalRecordCheckDocumentFailed
    );
  }

  private async updateCriminalRecordDocument(
    criminalRecordCheckDocument: CriminalRecordCheckDocument
  ): Promise<void> {
    await this.utils.actionsToPromise(
      updateCriminalRecordCheckDocument({
        criminalRecordCheckDocument: {
          ...this.criminalRecordDocument,
          ...criminalRecordCheckDocument,
        },
        file: this.selectedFile,
      }),
      updateCriminalRecordCheckDocumentSuccess,
      updateCriminalRecordCheckDocumentFailed
    );
  }

  public onFileSelected(event: Event): void {
    try {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.selectedFile = target.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error selecting file:', e);
      this.error = 'Failed to select file. Please try again.';
    }
  }

  public closePreview(): void {
    this.uploadPreview = null;
    this.selectedFile = undefined;
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  public onFileDrop(event: DragEvent) {
    try {
      event.preventDefault();
      this.dragActive = false;
      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        this.selectedFile = event.dataTransfer.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error dropping file:', e);
      this.error = 'Failed to drop file. Please try again.';
    }
  }
}
